import { SCALAR_FIELDS } from "@/lib/graphql/serialize-input";
import { coerceBigFloatFieldsToNumber } from "@/lib/graphql/coerce-bigfloat-output";

// Canonical list of deal BigFloat/DECIMAL fields lives in the registry so this
// stays in sync with the schema (and picks up margin_percentage). See
// apps/hub/lib/graphql/serialize-input.ts.
const DECIMAL_FIELDS = SCALAR_FIELDS.deals.bigFloat;

/** Write direction: number → string for GraphQL BigFloat input. */
export function stringifyDecimalFields<T extends object>(input: T): T {
  const record = input as Record<string, unknown>;

  return {
    ...input,
    ...Object.fromEntries(
      DECIMAL_FIELDS.filter((field) => field in record).map((field) => [
        field,
        record[field] !== null && record[field] !== undefined ? String(record[field]) : null,
      ])
    ),
  };
}

/**
 * Read direction: BigFloat string → number so the `Deal` domain model's
 * `number`-typed amount fields are honest. Apollo returns these as strings;
 * without this `sum + deal.revenue` concatenates. Idempotent on numeric rows
 * (PostgREST / RPC). Apply at every boundary that casts raw rows to `Deal`.
 */
export function numberifyDecimalFields<T extends object>(input: T): T {
  return coerceBigFloatFieldsToNumber(input, DECIMAL_FIELDS);
}

/** Null/array-safe `numberifyDecimalFields` for query results. */
export function normalizeDeals<T>(rows: T): T {
  return (
    Array.isArray(rows)
      ? rows.map((row) => (row ? numberifyDecimalFields(row as object) : row))
      : rows
  ) as T;
}
